import { Component, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { NgxUiLoaderService, SPINNER } from 'ngx-ui-loader';
import Swal from 'sweetalert2';
import { MapsAPILoader } from '@agm/core';
import moment from 'moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare const google: any;

@Component({
  selector: 'app-create-job',
  templateUrl: './create-job.component.html',
  styleUrls: ['./create-job.component.css']
})
export class CreateJobComponent implements OnInit {
  form: FormGroup;
  submitted = false;
  spinnerType = SPINNER.circle;
  defaultBounds: any;
  boundsLimit = true;
  hasGoogleSelection = false;
  locationData: any = {};
  jobs: any[] = [];
  showCreateForm = false;
  pageSize: number = 6;
  page: number = 1;
  selectedJob: any = null;
  isEditMode = false;
  editingJobId: string | null = null;
  
  // Role options
  roleOptions = [
    'Executive / Senior-level Official',
    'Mid-level Official',
    'Manager / Supervisor',
    'Professional',
    'Technician / Specialist',
    'Creative',
    'Sales',
    'Administrative',
    'Assistant / Support Staff',
    'Customer Service',
    'Operations',
    'Driver',
    'Craft Worker / Tradesman',
    'Service Technician',
    'Laborer',
    'Hiring Manager',
    'Other'
  ];

  // Industry options
  industryOptions = [
    'Aerospace & Defense',
    'Agriculture',
    'Arts, Entertainment & Recreation',
    'Automotive',
    'Construction & Facility Services',
    'Education',
    'Energy, Mining & Utilities',
    'Financial Services',
    'Government & Public Administration',
    'Heathcare',
    'Hotels, Travel & Hospitality',
    'Human Resources & Staffing',
    'Information Technology',
    'Insurance',
    'Legal',
    'Management & Consulting',
    'Manufacturing',
    'Media & Communication',
    'Nonprofit & NGO',
    'Personal Consumer Services',
    'Pharmaceutical & Biotechnology',
    'Real Estate',
    'Restaurants & Food Services',
    'Retail & Wholesale',
    'Telecommunications',
    'Transportation & Logistics',
    'Other'
  ];

  // Job Type options
  jobTypeOptions = [
    'Full-time',
    'Part-time',
    'Temporary / Gig',
    'Contract',
    'Internship',
    'Commission'
  ];

  // Remote Job options
  remoteJobOptions = ['No', 'Yes', 'Mixed', 'Temporarily'];

  // Application Receive Type options
  receiveTypeOptions = [
    'Application website',
    'Email',
    'Phone',
    'In-person'
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private ngxService: NgxUiLoaderService,
    private mapsAPILoader: MapsAPILoader,
    private zone: NgZone,
    private modalService: NgbModal
  ) {
    this.getLocation();
  }

  ngOnInit(): void {
    this.getJobPosts();
    this.form = this.fb.group({
      // Job Post Section - flat structure for easier form binding
      companyName: ['', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['Other'],
      phoneNumber: [''],
      companyValues: [''],
      companyWebsite: [''],
      companyWebsitePrivacy: [false],
      NamePrivacy: [false],
      EmailPrivacy: [false],
      PhonePrivacy: [false],

      // Job Basics Section
      industry: ['Other'],
      category: [''],
      title: ['', Validators.required],
      isAddress: [false],
      isContact: [false],
      location: this.fb.group({
        googleAddress: [''],
        streetAddress: [''],
        landMark: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        location: this.fb.group({
          type: ['Point'],
          coordinates: [[0, 0]]
        })
      }),
      remoteJob: ['No'],

      // Job Details Section
      jobType: ['Full-time'],
      jobPay: [''],
      jobDuties: [''],
      skills: [''],

      // Recruiting Address Section
      recruitingAddress: this.fb.group({
        address: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        contactNumber: [''],
        currentRegionStatus: [false],
        addressStatus: [false],
        contactStatus: [false]
      }),

      // Application Settings Section
      applicationSettings: this.fb.group({
        receiveType: ['Email'],
        receiveValue: ['', Validators.required],
        documents: [''],
        duration: [''] // Backend calculates activeTill from this
      })
      // Note: activeTill is calculated by backend from duration
      // Note: createdBy is set by backend from authenticated user
    });
  }

  getLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.defaultBounds = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        },
        () => {
          this.defaultBounds = { lat: 0, lng: 0 };
        }
      );
    } else {
      this.defaultBounds = { lat: 0, lng: 0 };
    }
  }

  get f() {
    return this.form.controls;
  }

  onLocationChange(addressData: any) {
    this.zone.run(() => {
      this.hasGoogleSelection = true;
      this.locationData = addressData;
      
      const locationGroup = this.form.get('location') as FormGroup;
      if (locationGroup) {
        locationGroup.patchValue({
          googleAddress: addressData.googleAddress || '',
          streetAddress: addressData.streetAddress || '',
          city: addressData.city || '',
          state: addressData.state || '',
          postalCode: addressData.postalCode || '',
          country: addressData.country || '',
          location: {
            type: 'Point',
            coordinates: addressData.location?.coordinates || [0, 0]
          }
        });
      }
    });
  }

  onSubmit() {
    this.submitted = true;

    if (this.form.invalid) {
      Swal.fire('Error', 'Please fill in all required fields.', 'error');
      return;
    }

    // Validate application settings receiveValue based on receiveType
    const receiveType = this.form.get('applicationSettings.receiveType')?.value;
    const receiveValue = this.form.get('applicationSettings.receiveValue')?.value;
    
    if (!receiveValue) {
      Swal.fire('Error', 'Please provide application contact information.', 'error');
      return;
    }

    // Build the job object according to schema
    // Note: Backend will reverse coordinates and calculate activeTill from duration
    const locationValue = this.form.get('location')?.value;
    
    // Structure location for backend: jobBasics.location.location.coordinates
    let locationData = null;
    if (locationValue && locationValue.location?.coordinates?.length === 2) {
      locationData = {
        location: {
          type: 'Point',
          coordinates: locationValue.location.coordinates
        }
      };
    }
    
    const jobData = {
      jobPost: {
        companyName: this.form.get('companyName')?.value,
        name: this.form.get('name')?.value,
        email: this.form.get('email')?.value,
        role: this.form.get('role')?.value,
        phoneNumber: this.form.get('phoneNumber')?.value || '',
        companyValues: this.form.get('companyValues')?.value || '',
        companyWebsite: this.form.get('companyWebsite')?.value || null,
        companyWebsitePrivacy: this.form.get('companyWebsitePrivacy')?.value || false,
        NamePrivacy: this.form.get('NamePrivacy')?.value || false,
        EmailPrivacy: this.form.get('EmailPrivacy')?.value || false,
        PhonePrivacy: this.form.get('PhonePrivacy')?.value || false
      },
      jobBasics: {
        industry: this.form.get('industry')?.value,
        category: this.form.get('category')?.value || null,
        title: this.form.get('title')?.value,
        location: locationData,
        remoteJob: this.form.get('remoteJob')?.value
      },
      jobDetails: {
        jobType: this.form.get('jobType')?.value,
        jobPay: this.form.get('jobPay')?.value || '',
        jobDuties: this.form.get('jobDuties')?.value || '',
        skills: this.form.get('skills')?.value || ''
      },
      recruitingAddress: this.form.get('recruitingAddress')?.value,
      applicationSettings: {
        receiveType: receiveType,
        receiveValue: receiveValue,
        documents: this.form.get('applicationSettings.documents')?.value || '',
        duration: this.form.get('applicationSettings.duration')?.value || null
      }
      // Note: activeTill is calculated by backend from duration, so we don't send it
      // Note: createdBy is set by backend from authenticated user
    };

    this.ngxService.start();
    
    if (this.isEditMode && this.editingJobId) {
      // Update existing job
      this.authenticationService.updateJob(this.editingJobId, jobData).subscribe(
        (response: any) => {
          this.ngxService.stop();
          const message = response?.message || 'Job updated successfully';
          Swal.fire('Success', message, 'success').then(() => {
            this.resetForm();
            this.getJobPosts(); // Refresh jobs list
          });
        },
        (error) => {
          this.ngxService.stop();
          const errorMessage = error.error?.message || error.error?.error || 'Failed to update job posting.';
          Swal.fire('Error', errorMessage, 'error');
        }
      );
    } else {
      // Create new job
      this.authenticationService.createJob(jobData).subscribe(
        (response: any) => {
          this.ngxService.stop();
          const message = response?.message || 'Job created successfully';
          Swal.fire('Success', message, 'success').then(() => {
            this.resetForm();
            this.getJobPosts(); // Refresh jobs list
          });
        },
        (error) => {
          this.ngxService.stop();
          const errorMessage = error.error?.message || error.error?.error || 'Failed to create job posting.';
          Swal.fire('Error', errorMessage, 'error');
        }
      );
    }
  }

  getPlaceholderText(): string {
    const receiveType = this.form.get('applicationSettings.receiveType')?.value;
    switch (receiveType) {
      case 'Application website':
        return 'https://example.com/apply';
      case 'Email':
        return 'email@example.com';
      case 'Phone':
        return '(555) 123-4567';
      case 'In-person':
        return 'Address or location';
      default:
        return 'Contact information';
    }
  }

  cancel() {
    this.resetForm();
  }

  resetForm() {
    this.showCreateForm = false;
    this.submitted = false;
    this.isEditMode = false;
    this.editingJobId = null;
    this.hasGoogleSelection = false;
    if (this.form) {
      this.form.reset();
      // Reset to default values
      this.form.patchValue({
        role: 'Other',
        industry: 'Other',
        remoteJob: 'No',
        jobType: 'Full-time',
        receiveType: 'Email'
      });
    }
  }

  editJob(job: any) {
    this.isEditMode = true;
    this.editingJobId = job._id || job.id;
    this.showCreateForm = true;
    this.populateFormWithJobData(job);
  }

  populateFormWithJobData(job: any) {
    if (!job || !this.form) return;

    // Populate Job Post Section
    if (job.jobPost) {
      this.form.patchValue({
        companyName: job.jobPost.companyName || '',
        name: job.jobPost.name || '',
        email: job.jobPost.email || '',
        role: job.jobPost.role || 'Other',
        phoneNumber: job.jobPost.phoneNumber || '',
        companyValues: job.jobPost.companyValues || '',
        companyWebsite: job.jobPost.companyWebsite || '',
        companyWebsitePrivacy: job.jobPost.companyWebsitePrivacy || false,
        NamePrivacy: job.jobPost.NamePrivacy || false,
        EmailPrivacy: job.jobPost.EmailPrivacy || false,
        PhonePrivacy: job.jobPost.PhonePrivacy || false
      });
    }

    // Populate Job Basics Section
    if (job.jobBasics) {
      this.form.patchValue({
        industry: job.jobBasics.industry || 'Other',
        category: job.jobBasics.category || '',
        title: job.jobBasics.title || '',
        remoteJob: job.jobBasics.remoteJob || 'No',
        isAddress: job.jobBasics.isAddress || false,
        isContact: job.jobBasics.isContact || false
      });

      // Populate location if available
      if (job.jobBasics.location) {
        const location = job.jobBasics.location;
        if (location.location && location.location.coordinates && location.location.coordinates.length === 2) {
          this.hasGoogleSelection = true;
          this.form.patchValue({
            location: {
              googleAddress: location.googleAddress || location.streetAddress || '',
              streetAddress: location.streetAddress || '',
              landMark: location.landMark || '',
              city: location.city || '',
              state: location.state || '',
              postalCode: location.postalCode || '',
              country: location.country || '',
              location: {
                type: 'Point',
                coordinates: location.location.coordinates
              }
            }
          });
        }
      }
    }

    // Populate Job Details Section
    if (job.jobDetails) {
      this.form.patchValue({
        jobType: job.jobDetails.jobType || 'Full-time',
        jobPay: job.jobDetails.jobPay || '',
        jobDuties: job.jobDetails.jobDuties || '',
        skills: job.jobDetails.skills || ''
      });
    }

    // Populate Recruiting Address Section
    if (job.recruitingAddress) {
      this.form.patchValue({
        recruitingAddress: {
          address: job.recruitingAddress.address || '',
          city: job.recruitingAddress.city || '',
          state: job.recruitingAddress.state || '',
          postalCode: job.recruitingAddress.postalCode || '',
          country: job.recruitingAddress.country || '',
          contactNumber: job.recruitingAddress.contactNumber || '',
          currentRegionStatus: job.recruitingAddress.currentRegionStatus || false,
          addressStatus: job.recruitingAddress.addressStatus || false,
          contactStatus: job.recruitingAddress.contactStatus || false
        }
      });
    }

    // Populate Application Settings Section
    if (job.applicationSettings) {
      this.form.patchValue({
        applicationSettings: {
          receiveType: job.applicationSettings.receiveType || 'Email',
          receiveValue: job.applicationSettings.receiveValue || '',
          documents: job.applicationSettings.documents || '',
          duration: job.applicationSettings.duration || ''
        }
      });
    }
  }

  getJobPosts() {
    this.ngxService.start();
    this.authenticationService.getJobPosts().subscribe(
      (response: any) => {
        console.log('API Response:', response);
        // Handle different response structures
        if (Array.isArray(response)) {
          // Direct array response
          this.jobs = response;
        } else if (Array.isArray(response?.items)) {
          // Response with items array (current API structure)
          this.jobs = response.items;
        } else if (Array.isArray(response?.data)) {
          // Response with data property
          this.jobs = response.data;
        } else {
          this.jobs = [];
        }
        console.log('Jobs array:', this.jobs);
        this.page = 1;
        this.ngxService.stop();
      },
      (error) => {
        this.ngxService.stop();
        console.error('Error loading jobs:', error);
        Swal.fire('Error', error.error?.message || 'Failed to load jobs.', 'error');
      }
    );
  }

  toggleCreateForm() {
    this.showCreateForm = !this.showCreateForm;
    if (this.showCreateForm && !this.form) {
      // Form is initialized in ngOnInit, so this shouldn't be needed
      // But keeping it as a safety check
    }
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.jobs?.length || 0) / this.pageSize));
  }

  get pagedJobs(): any[] {
    const start = (Math.max(1, this.page) - 1) * this.pageSize;
    return (this.jobs || []).slice(start, start + this.pageSize);
  }

  prevPage(): void {
    if (this.page > 1) this.page -= 1;
  }

  nextPage(): void {
    if (this.page < this.totalPages) this.page += 1;
  }

  formatDate(date: any): string {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY');
  }

  isJobExpired(activeTill: any): boolean {
    if (!activeTill) return false;
    return moment(activeTill).isBefore(moment());
  }

  getJobLocation(job: any): string {
    // Check if it's a remote job first
    if (job?.jobBasics?.remoteJob === 'Yes') {
      return 'Remote';
    }
    
    // Check recruiting address first (more specific)
    if (job?.recruitingAddress?.address) {
      const addr = job.recruitingAddress;
      const parts = [addr.address, addr.city, addr.state, addr.country].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : addr.address;
    }
    
    // Check job basics location
    if (job?.jobBasics?.location?.streetAddress) {
      return job.jobBasics.location.streetAddress;
    }
    if (job?.jobBasics?.location?.city) {
      return job.jobBasics.location.city;
    }
    
    return '-';
  }

  openJobDetails(content: any, job: any) {
    this.selectedJob = job;
    this.modalService.open(content, { 
      ariaLabelledBy: 'job-details-modal',
      size: 'xl',
      windowClass: 'job-details-modal',
      centered: true
    });
  }

  getFullAddress(job: any): string {
    if (job?.recruitingAddress) {
      const addr = job.recruitingAddress;
      const parts = [
        addr.address,
        addr.city,
        addr.state,
        addr.postalCode,
        addr.country
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '-';
    }
    return '-';
  }
}
